import { useEffect, useMemo, useState } from "react";
import { api, resolveFileUrl } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { SchoolSelect, useSchools } from "../../components/SchoolSelect";
import { UserCircle2, Users, Clock, CalendarDays, LogIn, LogOut, RefreshCw } from "lucide-react";
import { localName, localFullName } from "../../lib/localName";

const POLL_MS = 30_000;

function fmtDateAR(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("ar-SA", { year: "numeric", month: "long", day: "numeric", weekday: "long" });
  } catch { return iso; }
}

function EmpAvatar({ emp, dotColor, lang }) {
  const name = localFullName(emp, lang) || "—";
  const initials = name.trim().slice(0, 1).toUpperCase();
  return (
    <div className="relative w-20 h-20 shrink-0">
      <div
        className={`absolute inset-0 rounded-full ring-4 ring-offset-2 ring-offset-card ${dotColor === "green" ? "ring-emerald-500" : "ring-rose-500"}`}
      />
      <div className="absolute inset-1 rounded-full bg-primary/10 text-primary grid place-items-center overflow-hidden font-bold text-2xl">
        {emp.photo_url
          ? <img src={resolveFileUrl(emp.photo_url)} alt="" className="w-full h-full object-cover" />
          : <span>{initials}</span>}
      </div>
      <span className={`absolute -bottom-0.5 -end-0.5 w-5 h-5 rounded-full border-2 border-card ${dotColor === "green" ? "bg-emerald-500" : "bg-rose-500"} shadow`} />
    </div>
  );
}

function PresentCard({ emp, todayIso, t, lang }) {
  const late = (emp.late_min || 0) > 0;
  return (
    <Card
      className="p-5 border border-emerald-500/30 bg-gradient-to-br from-emerald-50 to-transparent dark:from-emerald-950/20 rounded-2xl shadow-sm hover:shadow-md transition"
      data-testid={`att-present-${emp.id}`}
    >
      <div className="flex flex-col items-center text-center gap-3">
        <EmpAvatar emp={emp} dotColor="green" lang={lang} />
        <div className="min-w-0 w-full">
          <div className="font-semibold text-base truncate">{localFullName(emp, lang)}</div>
          <div className="text-xs text-muted-foreground truncate">{emp.position || "—"}</div>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <CalendarDays className="w-3.5 h-3.5" />
          <span>{fmtDateAR(todayIso)}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 w-full pt-2 border-t border-border/60">
          <div className="flex flex-col items-center py-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><LogIn className="w-3 h-3" /> {t("hr_check_in") || "دخول"}</div>
            <div className={`font-mono font-semibold tabular-nums mt-0.5 ${late ? "text-amber-600" : "text-emerald-600"}`}>
              {emp.check_in || "—"}
            </div>
          </div>
          <div className="flex flex-col items-center py-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><LogOut className="w-3 h-3" /> {t("hr_check_out") || "خروج"}</div>
            <div className="font-mono font-semibold tabular-nums mt-0.5 text-primary">
              {emp.check_out || "—"}
            </div>
          </div>
        </div>
        {late && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 font-semibold">
            {t("late") || "متأخر"} · +{emp.late_min}د
          </span>
        )}
      </div>
    </Card>
  );
}

function AbsentCard({ emp, t, lang }) {
  return (
    <Card
      className="p-4 border border-rose-500/25 bg-gradient-to-br from-rose-50/60 to-transparent dark:from-rose-950/15 rounded-2xl shadow-sm hover:shadow-md transition"
      data-testid={`att-absent-${emp.id}`}
    >
      <div className="flex flex-col items-center text-center gap-2">
        <EmpAvatar emp={emp} dotColor="red" lang={lang} />
        <div className="min-w-0 w-full">
          <div className="font-semibold text-sm truncate">{localFullName(emp, lang)}</div>
          <div className="text-xs text-muted-foreground truncate">{emp.position || "—"}</div>
        </div>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-400 font-medium">
          {t("hr_not_checked_in_yet") || "لم يسجّل الدخول بعد"}
        </span>
      </div>
    </Card>
  );
}

export default function AttendanceTodayPage() {
  const {  t , lang } = useI18n();
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const { schools, schoolId, setSchoolId, isSuper, queryId } = useSchools();
  const [data, setData] = useState({ present: [], absent: [], date: "", counts: { present: 0, absent: 0, total: 0 } });
  const [lastRefresh, setLastRefresh] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const qs = queryId ? `?school_id=${queryId}` : "";
      const r = await api.get(`/hr/attendance/today${qs}`);
      setData(r.data || { present: [], absent: [] });
      setLastRefresh(new Date());
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [queryId]);
  useEffect(() => {
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [queryId]);

  const attendanceRate = useMemo(() => {
    const total = data.counts?.total || 0;
    if (!total) return 0;
    return Math.round(((data.counts?.present || 0) / total) * 100);
  }, [data]);

  return (
    <div className="space-y-6">
      {/* Hero */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="relative bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground p-6 sm:p-8">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 80% 0%, rgba(255,255,255,.4), transparent 40%)" }} />
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur grid place-items-center shrink-0">
                <Users className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">{t("hr_today_attendance") || "حضور الموظفين اليوم"}</h1>
                <p className="text-sm text-primary-foreground/80 mt-1">
                  {fmtDateAR(data.date)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-end">
                <div className="text-3xl font-bold tabular-nums">{attendanceRate}%</div>
                <div className="text-xs text-primary-foreground/80">{t("attendance") || "نسبة الحضور"}</div>
              </div>
              <Button
                onClick={load}
                variant="secondary"
                className="bg-white/20 hover:bg-white/30 backdrop-blur border border-white/30 text-primary-foreground"
                data-testid="att-refresh-btn"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Filter bar */}
      <Card className="p-4 border shadow-none flex flex-wrap items-center gap-3">
        <Users className="w-4 h-4 text-primary shrink-0" />
        <span className="text-sm font-medium text-muted-foreground">{t("select_school")}</span>
        <SchoolSelect
          schools={schools}
          value={schoolId}
          onChange={setSchoolId}
          testId="att-school-select"
          disabled={!isSuper}
        />
        <div className="flex-1" />
        {lastRefresh && (
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            <span>{t("last_updated") || "آخر تحديث"}: {lastRefresh.toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
          </div>
        )}
      </Card>

      {/* Present section */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            {t("hr_present_today") || "الحاضرون اليوم"}
            <span className="text-sm text-muted-foreground font-normal">({data.counts?.present || 0})</span>
          </h2>
        </div>
        {(data.present || []).length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground border-dashed" data-testid="att-present-empty">
            <UserCircle2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
            {t("hr_no_present_yet") || "لم يسجّل أي موظف الدخول بعد"}
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="att-present-grid">
            {data.present.map((e) => <PresentCard key={e.id} emp={e} todayIso={data.date} t={t} lang={lang} />)}
          </div>
        )}
      </section>

      {/* Absent section */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
            {t("hr_absent_today") || "الغائبون / لم يسجلوا بعد"}
            <span className="text-sm text-muted-foreground font-normal">({data.counts?.absent || 0})</span>
          </h2>
        </div>
        {(data.absent || []).length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground border-dashed" data-testid="att-absent-empty">
            <UserCircle2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
            {t("hr_all_present") || "الجميع سجّلوا الدخول ✓"}
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="att-absent-grid">
            {data.absent.map((e) => <AbsentCard key={e.id} emp={e} t={t} lang={lang} />)}
          </div>
        )}
      </section>
    </div>
  );
}
